import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.elfazaa.com";

// Mapping pathname → libellé humain pour le fil d'Ariane structuré.
// Les routes absentes héritent du dernier segment d'URL formaté.
const ROUTE_LABELS: Record<string, string> = {
  "/": "Accueil",
  "/notre-histoire": "Notre Histoire",
  "/contact": "Contact",
  "/install": "Installer l'app",
  "/merci": "Merci",
};

// Routes pour lesquelles un BreadcrumbList plus spécifique est déjà injecté
// par la page elle-même (ex: ProductDetail). On ne les écrase pas.
const PAGES_WITH_OWN_BREADCRUMB = [/^\/product\//];

const formatSegment = (seg: string) =>
  decodeURIComponent(seg)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Injecte un schema BreadcrumbList global sur toutes les pages publiques
 * qui n'ont pas déjà leur propre fil d'Ariane structuré.
 * S'attache au <head> via un <script id="global-breadcrumb-jsonld">.
 */
export const GlobalBreadcrumbSchema = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const ID = "global-breadcrumb-jsonld";
    const existing = document.getElementById(ID);

    // Si la page gère déjà son propre breadcrumb, on retire le global et on s'arrête
    if (PAGES_WITH_OWN_BREADCRUMB.some((rx) => rx.test(pathname))) {
      existing?.remove();
      return;
    }

    // Construit la liste : Accueil → ... → Page courante
    const segments = pathname.split("/").filter(Boolean);
    const items: Array<{ name: string; url: string }> = [
      { name: "Accueil", url: `${SITE_URL}/` },
    ];

    let cumulative = "";
    segments.forEach((seg) => {
      cumulative += `/${seg}`;
      const name = ROUTE_LABELS[cumulative] ?? formatSegment(seg);
      items.push({ name, url: `${SITE_URL}${cumulative}` });
    });

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: it.url,
      })),
    };

    existing?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = ID;
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.getElementById(ID)?.remove();
    };
  }, [pathname]);

  return null;
};
